import type { GetApiV1ThreeLetterCodePartnerAdminsContractPlans200 } from '@fukuri-admins/apis/client/gen/model';
import { useNavigate } from '@fukuri-admins/hooks/common';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ContractPlanListPresentation } from '../presentations';
import { useGetPartnerAdminsContractPlans } from './useGetPartnerAdminsContractPlans';

type PresentationProps = React.ComponentProps<typeof ContractPlanListPresentation>;

type IUseContractPlanList = () => {
  state: {
    /** API レスポンス全体 */
    data: GetApiV1ThreeLetterCodePartnerAdminsContractPlans200;
    /** 現在のページ番号（1始まり） */
    currentPage: number;
    /** 総ページ数 */
    totalPage: number;
    /** 総件数 */
    totalCount: number;
    /** 検索 Input の現在値（制御コンポーネント用） */
    searchValue: string;
    /**
     * 検索ボタン押下済み（committedFilterParams に name が存在する）かどうか
     *
     * 検索結果 0 件と全件 0 件（Empty 状態）を区別するために使用する
     */
    isFiltered: boolean;
  };
  actions: {
    /** ページ変更時のハンドラ */
    handlePageChange: PresentationProps['pagerProps']['onPageChange'];
    /**
     * 行クリック時のハンドラ（契約プラン詳細画面への遷移）
     *
     * @param planId 操作対象の契約プラン ID
     */
    handleRowClick: (planId: number) => void;
    /**
     * 検索 Input の値変更時のハンドラ（API は発火させない）
     *
     * @param value 入力値
     */
    handleSearchChange: (value: string) => void;
    /** 検索ボタン押下時のハンドラ（committedFilterParams を更新して API を発火させる） */
    handleSearch: () => void;
    /** 検索クリアボタン（×）押下時のハンドラ（name をリセットして全件表示に戻す） */
    handleSearchClear: () => void;
  };
};

/**
 * 契約プラン一覧の状態管理とアクションを提供するコンポーネントフック
 *
 * - URL クエリパラメータ（name / page）で検索・ページング状態を管理する
 * - filterParams（useSearchParams）: 入力途中の draft 値。replace:true で URL に書くが API は発火しない
 * - committedFilterParams（useState）: 検索ボタン押下で確定した値。SWR キーとして API を発火する
 */
export const useContractPlanList: IUseContractPlanList = () => {
  const navigate = useNavigate();
  const [filterParams, setFilterParams] = useSearchParams();
  const threeLetterCode = getThreeLetterCode() ?? '';

  // committedFilterParams（useState）: useGetPartnerAdminsContractPlans に渡す API 用パラメータ。
  // 検索ボタン押下・ページ変更時のみ更新することで「検索ボタン押下時のみ API 発火」を実現する。
  const [committedFilterParams, setCommittedFilterParams] = useState(
    () => new URLSearchParams(filterParams)
  );

  // URL クエリパラメータから検索フォームの draft 値を導出する（filterParams から読む）
  const searchValue = filterParams.get('name') || '';

  // committedFilterParams の name で判定する（入力途中の filterParams ではなく、
  // 実際に API が発火した検索条件があるかどうかを表す）
  const isFiltered = Boolean(committedFilterParams.get('name'));

  const currentPage = Number(committedFilterParams.get('page')) || 1;

  const { data, totalPage, totalCount } = useGetPartnerAdminsContractPlans({
    threeLetterCode,
    name: committedFilterParams.get('name') || undefined,
    page: committedFilterParams.get('page') || undefined,
  });

  /**
   * 入力欄変更時のハンドラ
   *
   * URL の name パラメータを replace: true で更新する（API は発火させない）
   *
   * @param value 入力値
   */
  const handleSearchChange = (value: string) => {
    const newParams = new URLSearchParams(filterParams);
    if (value) {
      newParams.set('name', value);
    } else {
      newParams.delete('name');
    }
    setFilterParams(newParams, { replace: true });
  };

  /**
   * 検索ボタン押下時のハンドラ
   *
   * page を削除し、filterParams を committedFilterParams にコピーして API を発火させる
   */
  const handleSearch = () => {
    const newParams = new URLSearchParams(filterParams);
    newParams.delete('page');
    setFilterParams(newParams, { replace: true });
    setCommittedFilterParams(newParams);
  };

  /**
   * 検索クリアボタン（×）押下時のハンドラ
   *
   * name と page を URL クエリから削除し、committedFilterParams も同期して全件表示に戻す
   */
  const handleSearchClear = () => {
    const newParams = new URLSearchParams(filterParams);
    newParams.delete('name');
    newParams.delete('page');
    setFilterParams(newParams, { replace: true });
    setCommittedFilterParams(newParams);
  };

  /**
   * ページを変更する
   *
   * committedFilterParams ベースで page を更新して API を発火させる
   *
   * @param newPage 遷移先のページ番号（1始まり）
   */
  const handlePageChange: PresentationProps['pagerProps']['onPageChange'] = newPage => {
    const newCommitted = new URLSearchParams(committedFilterParams);
    newCommitted.set('page', String(newPage));
    const newParams = new URLSearchParams(filterParams);
    newParams.set('page', String(newPage));
    setFilterParams(newParams);
    setCommittedFilterParams(newCommitted);
  };

  /**
   * 行クリック時に契約プラン詳細画面へ遷移する
   *
   * @param planId 遷移先の契約プラン ID
   */
  const handleRowClick = (planId: number): void => {
    navigate(`/contract-plans/${planId}`);
  };

  return {
    state: {
      data,
      currentPage,
      totalPage,
      totalCount,
      searchValue,
      isFiltered,
    },
    actions: {
      handlePageChange,
      handleRowClick,
      handleSearchChange,
      handleSearch,
      handleSearchClear,
    },
  };
};
